import { graphql } from 'gatsby';
import React from 'react';
import Box from '../../components/Box/Box';
import Image from '../../components/Image/Image';
import WithSection from '../../components/WithSection/WithSection';
import {
    Body1,
    Head3,
    Head4,
    Headline,
} from '../../components/Typography/Typography';
import PrismicLink from '../../components/PrismicLink/PrismicLink';
import Button from '../../components/Button/Button';
import Inline from '../../components/Inline/Inline';
import Stack from '../../components/Stack/Stack';
import useViewport from '../../hooks/useViewport';
import CtaSlots from '../../slots/CtaSlots/CtaSlots';
import { IoIosArrowForward } from 'react-icons/io';
import RichText from '../../components/RichText/RichText';
import { rem } from 'polished';
import { MobileStoreButtons } from '../../components/MobileStoreButtons/MobileStoreButtons';
import { SECTION_VERTICAL_PADDING } from '../../components/SectionWrapper/SectionWrapper';
import { isLightColor } from '../../utils/isLightColor';

const layoutGridTemplateColumns = {
    'Half to half': '1fr 1fr',
    'Three quarters to a quarters': '1fr 1fr 1fr 1fr 1fr',
};
const getLayoutGridTemplateAreas = (isReversed) => ({
    'Half to half': isReversed ? '"image content"' : '"content image"',
    'Three quarters to a quarters': isReversed
        ? '"image image content content content"'
        : '"content content content image image"',
});
const DEFAULT_LAYOUT = 'Three quarters to a quarters';

const BodySection = ({ data: { primary, items } }) => {
    const { isMobile } = useViewport();
    const {
        card_background_color,
        card_text_color,
        card_text_accent_color,
        store_buttons,
        layout,
    } = primary;
    const isCard = primary.card_background_color;
    const isCardBgDark = !isLightColor(primary.card_background_color);

    return (
        <Box
            gridColumn={{ _: '1/-1', lg: '2/-2' }}
            display="flex"
            flexDirection="column"
            gap={rem(50)}
        >
            {primary?.heading.text || primary?.subheading ? (
                <Box>
                    {primary?.heading && (
                        <Head3
                            fontSize="18px"
                            textAlign={['start', 'center']}
                            bold
                        >
                            {primary?.heading.text}
                        </Head3>
                    )}
                    {primary?.subheading && (
                        <Body1 marginTop="20px" textAlign={['start', 'center']}>
                            {primary?.subheading}
                        </Body1>
                    )}
                </Box>
            ) : null}

            <Box
                display="flex"
                flexDirection="column"
                gap={rem(60)}
                {...(isCard &&
                    card_background_color !== 'none' && {
                        backgroundColor: card_background_color,
                        color: card_text_color,
                        borderRadius: 'lg',
                        py: { _: rem(32), lg: rem(60) },
                        px: { _: rem(16), lg: rem(60) },
                    })}
            >
                {items.map(
                    ({
                        is_reversed,
                        hero_title,
                        title,
                        subtitle,
                        section_description,
                        image,
                        text_link,
                        link,
                        cta,
                        inline_video_mp4,
                        inline_video_webm,
                    }) => (
                        <Box
                            display="grid"
                            alignItems="center"
                            gridTemplateColumns={{
                                sm: isCard
                                    ? '1fr 1fr'
                                    : layoutGridTemplateColumns[
                                          layout || DEFAULT_LAYOUT
                                      ],
                            }}
                            gridTemplateAreas={{
                                _: `"content" "image"`,
                                sm: is_reversed
                                    ? `${
                                          isCard
                                              ? '"image content"'
                                              : getLayoutGridTemplateAreas(
                                                    is_reversed
                                                )[layout || DEFAULT_LAYOUT]
                                      }`
                                    : `${
                                          isCard
                                              ? '"content image"'
                                              : getLayoutGridTemplateAreas(
                                                    is_reversed
                                                )[layout || DEFAULT_LAYOUT]
                                      }`,
                            }}
                            gridGap={{ _: rem(30), md: rem(60) }}
                        >
                            <Box display="grid" gridArea="content">
                                {subtitle && (
                                    <Headline
                                        mb="8px"
                                        color={
                                            card_text_accent_color
                                                ? card_text_accent_color
                                                : 'secondary.1200'
                                        }
                                    >
                                        {subtitle}
                                    </Headline>
                                )}
                                <Stack space="24px">
                                    {hero_title?.text && (
                                        <Head3 mb={rem(32)}>
                                            {hero_title.text}
                                        </Head3>
                                    )}
                                    {title?.text && (
                                        <Head4 bold>{title?.text}</Head4>
                                    )}
                                    <RichText
                                        render={section_description.richText}
                                    />
                                    {text_link && (
                                        <>
                                            {/*{isMobile ? (*/}
                                            {false ? (
                                                <PrismicLink link={link}>
                                                    <Button
                                                        variant="secondary"
                                                        isLight={isCardBgDark}
                                                    >
                                                        <Box
                                                            display="flex"
                                                            alignItems="center"
                                                            gap={rem(10)}
                                                        >
                                                            <span>
                                                                {text_link}
                                                            </span>
                                                            <IoIosArrowForward />
                                                        </Box>
                                                    </Button>
                                                </PrismicLink>
                                            ) : (
                                                <PrismicLink
                                                    link={link}
                                                    variant="underline"
                                                    color={
                                                        card_text_accent_color
                                                            ? card_text_accent_color
                                                            : 'primary.main'
                                                    }
                                                >
                                                    {text_link}
                                                </PrismicLink>
                                            )}
                                        </>
                                    )}
                                    {cta?.document && (
                                        <Box>
                                            <Inline
                                                gap="8px"
                                                display="flex"
                                                justifyContent="start"
                                                alignItems="center"
                                                marginBottom="27px"
                                            >
                                                <CtaSlots CTA={cta} />
                                            </Inline>
                                        </Box>
                                    )}

                                    {store_buttons.document ? (
                                        <MobileStoreButtons
                                            data={store_buttons.document.data}
                                        />
                                    ) : null}
                                </Stack>
                            </Box>
                            <Box
                                gridArea="image"
                                borderRadius="sm"
                                overflow="hidden"
                                {...(!isCard && { px: rem(32) })}
                                justifySelf={
                                    is_reversed
                                        ? { _: 'center', sm: 'start' }
                                        : { _: 'center', sm: 'end' }
                                }
                            >
                                {inline_video_webm.url &&
                                inline_video_mp4.url ? (
                                    <Box
                                        as="video"
                                        borderRadius="md"
                                        width="100%"
                                        height="auto"
                                        loop
                                        muted
                                        playsInline
                                        autoPlay
                                        poster={
                                            image.gatsbyImageData.images
                                                .fallback.src
                                        }
                                    >
                                        <source
                                            src={inline_video_webm.url}
                                            type="video/webm"
                                        />
                                        <source
                                            src={inline_video_mp4.url}
                                            type="video/mp4"
                                        />
                                    </Box>
                                ) : (
                                    <Image img={image} width="100%" />
                                )}
                            </Box>
                        </Box>
                    )
                )}
            </Box>
        </Box>
    );
};

// TODO: convert to fragments
export const _ = graphql`
    fragment BODY_SECTION on PrismicSplitDefault {
        primary {
            __typename
            heading {
                text
            }
            subheading
            card_background_color
            card_text_color
            card_text_accent_color
            layout
            store_buttons {
                document {
                    ... on PrismicMobileStoreButtons {
                        id
                        data {
                            app_store_icon {
                                url
                                alt
                            }
                            app_store_link {
                                ...PRISMIC_LINK_FRAGMENT
                            }
                            google_store_icon {
                                alt
                                url
                            }
                            google_store_link {
                                ...PRISMIC_LINK_FRAGMENT
                            }
                        }
                    }
                }
            }
        }
        items {
            hero_title {
                text
            }
            title {
                text
            }
            section_description: description {
                richText
            }
            is_reversed
            text_link
            subtitle
            link {
                ...PRISMIC_LINK_FRAGMENT
            }
            image {
                gatsbyImageData(layout: CONSTRAINED)
                alt
                url
            }
            inline_video_mp4 {
                ...PRISMIC_LINK_FRAGMENT
            }
            inline_video_webm {
                ...PRISMIC_LINK_FRAGMENT
            }
            cta {
                document {
                    ... on PrismicCta {
                        data {
                            body {
                                ... on PrismicCtaDataBodyIconButton {
                                    items {
                                        __typename
                                        tracking_event
                                        icon {
                                            gatsbyImageData(layout: CONSTRAINED)
                                            alt
                                            url
                                        }
                                    }
                                }
                                ... on PrismicCtaDataBodyButtons {
                                    items {
                                        __typename
                                        text
                                        size
                                        variant
                                        tracking_event
                                        link {
                                            ...PRISMIC_LINK_FRAGMENT
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
`;

export default WithSection(BodySection);
